import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Workflow, Loader2, ArrowRight, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BusinessProcesses = ({ project, landscape }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProcesses, setExpandedProcesses] = useState({});

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/discovery/business-processes`);
      setData(response.data);
      if (response.data.processes && response.data.processes.length > 0) {
        setExpandedProcesses({ [response.data.processes[0].name]: true });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProcess = (processName) => {
    setExpandedProcesses({
      ...expandedProcesses,
      [processName]: !expandedProcesses[processName]
    });
  };

  if (loading || !data) return <Loader2 className="w-12 h-12 animate-spin mx-auto mt-20" />;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <Workflow className="w-6 h-6 mr-3 text-indigo-600" />
        Business Process Flows with RICEFW Mapping
      </h3>
      
      {landscape && (
        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
          <p className="text-sm text-indigo-800">
            <strong>Analyzing:</strong> {landscape.name} - {landscape.systems_detail?.length || 0} systems
          </p>
        </div>
      )}
      
      <div className="space-y-6">
        {data.processes.map((process, idx) => {
          const isExpanded = expandedProcesses[process.name];
          
          return (
            <div key={idx} className="border-2 border-indigo-200 rounded-2xl overflow-hidden">
              <div 
                onClick={() => toggleProcess(process.name)}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 cursor-pointer hover:from-indigo-100 hover:to-purple-100 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-1">{process.name}</h4>
                    <p className="text-sm text-gray-600">Volume: {process.volume}</p>
                  </div>
                  <ChevronDown className={`w-6 h-6 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {isExpanded && (
                <div className="p-6 bg-white">
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      {process.steps.map((step, stepIdx) => (
                        <React.Fragment key={stepIdx}>
                          <div className="flex-1 min-w-0">
                            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl p-4 shadow-lg">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="w-6 h-6 bg-white text-indigo-600 rounded-full flex items-center justify-center font-bold text-sm">
                                  {stepIdx + 1}
                                </span>
                                <span className="font-bold text-sm">{step.step}</span>
                              </div>
                              <div className="mt-2 pt-2 border-t border-white/30">
                                <p className="text-xs opacity-90">T-Code:</p>
                                <p className="font-mono font-bold">{step.tcode}</p>
                              </div>
                              {step.ricefw && step.ricefw.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-white/30">
                                  <p className="text-xs opacity-90 mb-1">RICEFW:</p>
                                  {step.ricefw.map((obj, idx) => (
                                    <div key={idx} className="px-2 py-1 bg-white/20 rounded text-xs font-semibold mb-1 truncate">
                                      {obj}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {stepIdx < process.steps.length - 1 && (
                            <ArrowRight className="w-8 h-8 text-indigo-400 mx-2 flex-shrink-0" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  {process.issues && process.issues.length > 0 && (
                    <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
                      <p className="font-semibold text-yellow-900 mb-2">Migration Considerations:</p>
                      {process.issues.map((issue, idx) => (
                        <p key={idx} className="text-sm text-yellow-800">• {issue}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
